import React, { Component } from 'react';
import { SbEditableContent } from 'storyblok-react';
import { getComponent, blokToComponent } from '../components';
import { GoogleTagManager } from '../components/custom/google-tag-manager';
import { Jentis } from '../components/custom/jentis';

import { SEO } from '../components/custom/seo';
import { RcmCountrySwitchModal } from '../components/custom/country-switch-modal';
import { RcmIEModal } from '../components/custom/ie-modal';
import { markupFromRichtextField } from '../components/custom/richtext';
import { grabFundsProps } from '../services/component-mapping';
import { RcmUserSwitchModal } from '../components/custom/user-switch-modal';
import { DomService, StoryblokService, NavigationService, calculateReadingTime } from '../services';
import { StoryblokEntryProps, StoryblokEntryState } from './template.types';

const parseEntryData = ({ pageContext }: StoryblokEntryProps): StoryblokEntryState => {
  const story = {
    ...pageContext.story,
  };

  return {
    story,
    showIEModal: false,
    ...DomService.getGlobalConfig({
      pageId: story.uuid,
      locale: StoryblokService.getCountryCode(story).locale,
      country: StoryblokService.getCountryCode(story).country,
      websiteUrl: pageContext.websiteUrl,
      isArticle: story.content.component === 'article',
    }),
    globalContent: pageContext.globalContent,
    websiteUrl: pageContext.websiteUrl,
  };
};

const RcmGlobalConfig = getComponent('rcm-global-config') as React.ElementType;
const RcmGlobalContent = getComponent('rcm-global-content') as React.ElementType;
const Navigation = getComponent('rcm-navigation') as React.ElementType;
const Footer = getComponent('rcm-footer') as React.ElementType;
const Article = 'rcm-layout-article' as React.ElementType;
const Container = 'rcm-layout-container' as React.ElementType;
const FundsList = 'rcm-layout-funds' as React.ElementType;
const FundsDetail = 'rcm-layout-fund' as React.ElementType;
const Articles = 'rcm-layout-articles' as React.ElementType;
const Layout404 = 'rcm-layout-404' as React.ElementType;
const ContactButton = 'rcm-contact-button' as React.ElementType;
const DedicatedContainer = 'rcm-dedicated-container' as React.ElementType;
const FundsPrices = 'rcm-layout-fundsprices' as React.ElementType;
const FundsDocuments = 'rcm-layout-fundsdownloads' as React.ElementType;
const FundFusion = 'rcm-layout-fundsfusions' as React.ElementType;
const FundsMandatory = 'rcm-layout-fundsmandatory' as React.ElementType;
const FundsLetters = 'rcm-layout-fundsletters' as React.ElementType;
const Disclaimer = 'rcm-disclaimer-container' as React.ElementType;
const RcmNavigationSalzburg = 'rcm-navigation-salzburg' as React.ElementType;
const RcmNavigationGolfmeisterschaft = 'rcm-navigation-golfmeisterschaft' as React.ElementType;
const LicensorNotice = 'rcm-layout-licensor-notice' as React.ElementType;

// eslint-disable-next-line import/no-default-export
export default class StoryblokEntry extends Component<StoryblokEntryProps, StoryblokEntryState> {
  public static getDerivedStateFromProps(props: StoryblokEntryProps, state: StoryblokEntryState): StoryblokEntryState {
    return state.story.uuid !== props.pageContext.story.uuid ? parseEntryData(props) : null;
  }

  public constructor(props: StoryblokEntryProps) {
    super(props);
    this.state = parseEntryData(props);
  }

  public componentDidMount(): void {
    this.maskUrl();
    window.addEventListener('rcmLoginComplete', () => StoryblokService.redirect());

    /** fetch is polyfilled */
    // eslint-disable-next-line compat/compat
    fetch(`/navigation-data-${this.state.story.lang}.json`)
      .then((res) => res.json())
      .then((navigationData) => {
        this.setState({ navigation: navigationData });
      });

    NavigationService.getContactPage(this.state.story.lang).then((contactPage) => this.setState({ contact: contactPage }));

    DomService.activateConsentScript();

    const ua = window.navigator.userAgent;
    const isIE = ua.indexOf('MSIE ') > 0 || ua.indexOf('Trident/') > 0;
    this.setState({ showIEModal: isIE });
  }

  private maskUrl(): void {
    const { maskUrl } = this.props.location.state || {};
    return maskUrl ? window.history.replaceState('', '', maskUrl) : undefined;
  }

  public render(): JSX.Element {
    const { story, navigation, globalContent, showIEModal, websiteUrl, ...globalConfig } = this.state;

    if (story.content.component === 'page') {
      const nestableArticles = story.content.body?.find((item: SbEditableContent) => item.component === 'articles');
      if (nestableArticles) {
        nestableArticles.component = 'rcm-layout-articles';
      }
    }

    // intro of the page
    const getIntro = (intro: any) =>
      intro
        ? React.createElement('rcm-richtext', {
            // eslint-disable-next-line no-underscore-dangle
            slot: 'intro',
            'capitalize-first-Letter': undefined,
            'right-to-left': undefined,
            level: 1,
            width: 'full',
            'no-margin': true,
            dangerouslySetInnerHTML: {
              __html: markupFromRichtextField(intro),
            },
          })
        : '';

    return (
      <>
        <RcmGlobalConfig {...globalConfig} />
        <RcmGlobalContent globalContent={globalContent} />
        <GoogleTagManager googleTagManagerId={globalContent?.gtmId} />
        <Jentis globalConfig={globalConfig} />
        <SEO
          {...story.content.meta_tags}
          title={story.content.meta_tags?.title || story.name}
          nofollow={story.content.nofollow}
          noindex={story.content.noindex}
          lang={StoryblokService.getCountryCode(story).locale}
          slug={story.full_slug}
          authorized_roles={story.content.authorized_roles}
          alternates={story.alternates}
          websiteUrl={websiteUrl}
        />
        <RcmCountrySwitchModal globalContent={globalContent} alwaysOpen={StoryblokService.getCountryCode(story).countryCode === 'rcm-int'} />
        {StoryblokService.getCountryCode(story).countryCode !== 'not-found' && <RcmUserSwitchModal />}
        <RcmIEModal globalContent={globalContent} show={showIEModal} />
        {globalConfig.locale === 'rsi' && <RcmNavigationSalzburg />}

        {globalConfig.locale === 'golfmeisterschaft' && <RcmNavigationGolfmeisterschaft />}

        {!['rsi', 'golfmeisterschaft'].includes(globalConfig.locale) && (
          <Navigation
            tree={navigation}
            getComponent={getComponent}
            userTypeFromSlug={StoryblokService.getUserTypeFromSlug(story)}
            countryCode={StoryblokService.getCountryCode(story).countryCode}
            currentCountry={StoryblokService.getCountryCode(story).country}
            currentLanguage={StoryblokService.getCountryCode(story).locale}
            alternates={JSON.stringify(story.alternates)}
          />
        )}
        <main id="Content">
          <Container
            kind={globalConfig.locale === 'rsi' || globalConfig.locale === 'golfmeisterschaft' ? 'full' : 'normal'}
            is-article={story.content.component === 'article'}
            currentType={story.content.component}
            data-salzburg={globalConfig.locale === 'rsi'}
            data-golfmeisterschaft={globalConfig.locale === 'golfmeisterschaft'}
            has-prominent-campaign={story.content.has_prominent_campaign_banner}
            has-basic-campaign={story.content.has_basic_campaign_banner}
          >
            {story.content.component === 'article' && (
              <Article
                slot="content"
                article={JSON.stringify({
                  ...story.content,
                  readingTime: calculateReadingTime(story),
                })}
                story-uuid={story.uuid}
                country={StoryblokService.getCountryCode(story).country}
                language={StoryblokService.getCountryCode(story).locale}
              >
                {blokToComponent({ blok: story.content, getComponent })}
              </Article>
            )}
            {story.content.component === 'articles' && (
              <Articles
                slot="content"
                dropdown-label={story.content.dropdown_label}
                all-categories-label={story.content.all_categories_label}
                headline={story.content.headline}
                max-articles-number={story.content.max_articles_number}
                text={story.content.text}
                include-cee={story.content.include_cee}
              >
                {blokToComponent({ blok: story.content, getComponent })}
              </Articles>
            )}
            {story.content.component === 'pageNotFound' && (
              <div slot="content">
                <Layout404 />
              </div>
            )}

            {story.content.component === 'funds' && <FundsList slot="content" {...grabFundsProps(story.content)} />}
            {story.content.component === 'fund-detail' && (
              <FundsDetail slot="content">{blokToComponent({ blok: story.content, getComponent })}</FundsDetail>
            )}
            {story.content.component === 'courses-and-documents' && (
              <DedicatedContainer slot="content">{story.content?.body?.map((c) => blokToComponent({ blok: c, getComponent }))}</DedicatedContainer>
            )}
            {story.content.component === 'funds-prices' && (
              <DedicatedContainer slot="content">
                <FundsPrices
                  headline={story.content.headline}
                  input-placeholder={story.content.input_placeholder}
                  courses-and-documents-linktext={story.content.courses_and_documents_linktext}
                  courses-and-documents-link={story?.content?.courses_and_documents_link?.story?.full_slug}
                >
                  {getIntro(story.content.intro)}
                </FundsPrices>
              </DedicatedContainer>
            )}
            {story.content.component === 'funds-documents' && (
              <DedicatedContainer slot="content">
                <FundsDocuments
                  headline={story.content.headline}
                  input-placeholder={story.content.input_placeholder}
                  courses-and-documents-linktext={story.content.courses_and_documents_linktext}
                  courses-and-documents-link={story.content?.courses_and_documents_link?.story?.full_slug}
                >
                  {getIntro(story.content.intro)}
                </FundsDocuments>
              </DedicatedContainer>
            )}
            {story.content.component === 'fund-fusion' && (
              <DedicatedContainer slot="content">
                <FundFusion
                  headline={story.content.headline}
                  input-placeholder={story.content.input_placeholder}
                  no-funds-found-text={story.content.no_funds_found_text}
                  no-funds-found-headline={story.content.no_funds_found_headline}
                  courses-and-documents-linktext={story.content.courses_and_documents_linktext}
                  courses-and-documents-link={story?.content?.courses_and_documents_link?.story?.full_slug}
                >
                  {getIntro(story.content.intro)}
                </FundFusion>
              </DedicatedContainer>
            )}
            {story.content.component === 'funds-mandatory' && (
              <DedicatedContainer slot="content">
                <FundsMandatory
                  headline={story.content.headline}
                  input-placeholder={story.content.input_placeholder}
                  courses-and-documents-linktext={story.content.courses_and_documents_linktext}
                  courses-and-documents-link={story?.content?.courses_and_documents_link?.story?.full_slug}
                >
                  {getIntro(story.content.intro)}
                </FundsMandatory>
              </DedicatedContainer>
            )}
            {story.content.component === 'funds-letters' && (
              <DedicatedContainer slot="content">
                <FundsLetters
                  headline={story.content.headline}
                  input-placeholder={story.content.input_placeholder}
                  courses-and-documents-linktext={story.content.courses_and_documents_linktext}
                  courses-and-documents-link={story.content.courses_and_documents_link?.story?.full_slug}
                >
                  {getIntro(story.content.intro)}
                  {story.content.body.map((c) => blokToComponent({ blok: c, getComponent }))}
                </FundsLetters>
              </DedicatedContainer>
            )}
            {story.content.component === 'licensor-notice' && (
              <DedicatedContainer slot="content">
                <LicensorNotice headline={story.content.headline} />
              </DedicatedContainer>
            )}
            {story.content.component !== 'article' && story.content.component !== 'funds' && (
              <div slot="content">
                {blokToComponent({ blok: story.content, getComponent })}
                {story.content.disclaimer_type?.length > 0 && <Disclaimer disclaimer={JSON.stringify(story.content.disclaimer_type)} />}
              </div>
            )}
          </Container>
          {StoryblokService.getCountryCode(story).countryCode !== 'not-found' &&
            StoryblokService.getCountryCode(story).countryCode !== 'rcm-int' &&
            globalContent.contact.button.isEnabled === '1' && (
              <ContactButton link={globalContent?.contact?.button?.link} name={globalContent?.contact?.button?.name} />
            )}
          {story.content.ffquestionnaire?.content && blokToComponent({ blok: story.content.ffquestionnaire.content, getComponent })}
        </main>
        <Footer
          tree={navigation}
          getComponent={getComponent}
          userTypeFromSlug={StoryblokService.getUserTypeFromSlug(story)}
          countryCode={StoryblokService.getCountryCode(story).countryCode}
          isSalzburg={globalConfig.locale === 'rsi'}
        />
        {/* End Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${globalContent?.gtmId}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
        {/* End Google Tag Manager (noscript) */}
      </>
    );
  }
}
